/**
 * 통화 복구 유틸리티 (callRecovery.js)
 * 모바일 브라우저 절전/백그라운드 진입 등으로 실시간 call:offer 신호가 유실되었을 때,
 * 앱 활성화(마운트 또는 visibilityState === 'visible') 시점에 calls / call_participants DB를 조회하여
 * 진행 중인 수신 통화를 복구한다.
 */

/**
 * 조회된 통화 목록에서 started_at 내림차순(최신순)으로 정렬한 뒤
 * 28초 이내 조건을 만족하는 최신 1건을 선택하는 순수 함수
 * 
 * @param {Array<any>} rows - DB 조회 결과 배열
 * @param {string} userId - 현재 로그인 사용자 ID
 * @param {number} [now=Date.now()] - 기준 현재 시각 (ms)
 * @returns {{ callId: string, conversationId: string, callerId: string, kind: string, startedAt: string } | null}
 */
export function selectRecoverableCall(rows, userId, now = Date.now()) {
  if (!Array.isArray(rows) || rows.length === 0 || !userId) {
    return null;
  }

  // 1. 유효한 calls 및 started_at 이 존재하는 행만 필터링 및 시간 파싱
  const validItems = [];
  for (const item of rows) {
    const call = item?.calls;
    if (!call || call.status !== 'ringing' || call.caller_id === userId || !call.started_at) {
      continue;
    }

    const startedAtMs = new Date(call.started_at).getTime();
    if (Number.isNaN(startedAtMs)) {
      continue;
    }

    validItems.push({
      item,
      call,
      startedAtMs,
    });
  }

  if (validItems.length === 0) {
    return null;
  }

  // 2. started_at 기준 내림차순 (최신순) 정렬
  validItems.sort((a, b) => b.startedAtMs - a.startedAtMs);

  // 3. 28초 이내(0ms ~ 28000ms) 조건을 만족하는 최신 1건 선택 (발신측 30초 타임아웃 이전 건만 유효, §2-69 부활 차단)
  for (const { call, startedAtMs } of validItems) {
    const elapsedMs = now - startedAtMs;
    if (elapsedMs >= 0 && elapsedMs <= 28000) {
      return {
        callId: call.id,
        conversationId: call.conversation_id,
        callerId: call.caller_id,
        kind: call.kind || 'audio',
        startedAt: call.started_at,
      };
    }
  }

  return null;
}

/**
 * 진행 중인 수신 통화(ringing 상태) 조회 및 복구 대상 판정
 * 
 * 조건:
 * - call_participants.profile_id = userId
 * - call_participants.state = 'invited' (거절·이탈 제외)
 * - calls.status = 'ringing'
 * - calls.caller_id <> userId (자기 발신 건 제외)
 * - started_at 과 현재 시각 차이 <= 28초 (발신측 30초 타임아웃 이전 건만 유효, §2-69 부활 차단)
 * - started_at 내림차순 최신 1건
 * 
 * @param {import('@supabase/supabase-js').SupabaseClient} supabase
 * @param {string} userId
 * @returns {Promise<{ callId: string, conversationId: string, callerId: string, kind: string, startedAt: string } | null>}
 */
export async function fetchActiveIncomingCall(supabase, userId) {
  if (!supabase || !userId) return null;

  try {
    // call_participants 와 calls 의 외래키 관계(call_participants.call_id -> calls.id)를 통한 inner join 조회
    // (PostgREST 임베디드 정렬 문법 대신 JavaScript 레벨에서 started_at 내림차순 정렬 수행)
    const { data, error } = await supabase
      .from('call_participants')
      .select(`
        id,
        call_id,
        state,
        calls!inner (
          id,
          conversation_id,
          caller_id,
          kind,
          status,
          started_at
        )
      `)
      .eq('profile_id', userId)
      .eq('state', 'invited')
      .eq('calls.status', 'ringing')
      .neq('calls.caller_id', userId)
      .limit(5);

    if (error) {
      console.error('[callRecovery] 수신 통화 조회 실패:', error);
      throw error;
    }

    const candidate = selectRecoverableCall(data, userId);
    if (!candidate) return null;

    // [단체 통화 제외] 단체방 통화는 1:1 착신 복구로 집어 들지 않는다 (단체는 단체 엔진이 따로 처리)
    if (candidate.conversationId) {
      try {
        const { data: convRow, error: convErr } = await supabase
          .from('conversations')
          .select('type')
          .eq('id', candidate.conversationId)
          .maybeSingle();
        if (!convErr && convRow?.type === 'group') {
          console.info('[callRecovery] 단체방 통화라 1:1 복구에서 제외:', candidate.callId);
          return null;
        }
      } catch {
        // 조회에 실패하면 기존 1:1 동작을 그대로 유지한다
      }
    }

    // 차단된 통화 시도인지 확인 (내가 차단한 발신자의 통화 복구 차단)
    try {
      const { data: blockedRow, error: blockErr } = await supabase
        .from('blocked_call_attempts')
        .select('call_id')
        .eq('call_id', candidate.callId)
        .maybeSingle();

      if (!blockErr && blockedRow) {
        console.info('[callRecovery] 차단된 수신 통화 복구 제외:', candidate.callId);
        return null;
      }
    } catch (blockEx) {
      console.warn('[callRecovery] 차단 통화 확인 예외 (복구 계속 진행):', blockEx);
    }

    return candidate;
  } catch (err) {
    console.error('[callRecovery] 수신 통화 조회 예외 발생:', err);
    throw err;
  }
}
